import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, HostListener, inject, OnInit, untracked } from '@angular/core';
import { Router, RouterOutlet, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { SidebarComponent } from './layout/sidebar/sidebar.component';
import { HeaderComponent } from './layout/header/header.component';
import { BottomNavComponent } from './layout/bottom-nav/bottom-nav.component';
import { BidModalComponent } from './shared/bid-modal/bid-modal.component';
import { DirectBuyModalComponent } from './shared/direct-buy-modal/direct-buy-modal.component';
import { ToastComponent } from './shared/toast/toast.component';
import { FooterComponent } from './layout/footer/footer.component';
import { LoaderComponent } from './shared/loader/loader.component';
import { UpdateBannerComponent } from './shared/update-banner/update-banner.component';
import { TermsModalComponent } from './shared/terms-modal/terms-modal.component';
import { VehicleService } from './services/vehicle.service';
import { LoadingService } from './services/loading.service';
import { AuthService } from './services/auth.service';
import { TitleService } from './services/title.service';
import { InstellingenService } from './services/instellingen.service';
import { TermsService } from './services/terms.service';
import { UpdateCheckService } from './services/update-check.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SidebarComponent, HeaderComponent, BottomNavComponent, FooterComponent, BidModalComponent, DirectBuyModalComponent, ToastComponent, LoaderComponent, UpdateBannerComponent, TermsModalComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly vehicleService = inject(VehicleService);
  private readonly loadingService = inject(LoadingService);
  private readonly authService = inject(AuthService);
  // Injected for its side effect only: instantiates the router-driven document-title updates.
  private readonly titleService = inject(TitleService);
  // Injected for its side effect only: starts periodic service-worker update checks.
  private readonly updateCheckService = inject(UpdateCheckService);
  private readonly instellingenService = inject(InstellingenService);
  private readonly termsService = inject(TermsService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  public readonly isAuthPage = computed(() => {
    const url = this.currentUrl();
    return !this.authService.isAuthenticated() || url === '/login' || url === '/wachtwoord-vergeten' || url === '/wachtwoord-wijzigen' || url === '/bedrijf-selecteren';
  });

  constructor() {
    // Runs at startup and again whenever the user logs in or finishes selecting
    // a company during the session, so settings and the terms check are not
    // skipped after an in-app login. Terms/consent is scoped to the selected
    // company, so it must wait until the user profile is loaded and company
    // selection (if required) is complete - right after login, isAuthenticated
    // can flip to true before currentUser is fetched, so currentUser must be
    // checked explicitly rather than relying on needsCompanySelection() alone.
    effect(() => {
      const user = this.authService.currentUser();
      if (this.authService.isAuthenticated() && user && !this.authService.needsCompanySelection()) {
        untracked(() => {
          this.instellingenService.loadSettings();
          this.termsService.checkTerms();
        });
      }
    });
  }

  public ngOnInit(): void {
    // Initialize auth state from localStorage
    // AuthService constructor already handles this, but we can ensure it's called
    this.authService.checkLoginState();

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loadingService.start();
      }
      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.loadingService.stop();
      }
    });
  }

  @HostListener('document:keydown.escape')
  public handleEscape(): void {
    this.vehicleService.closeBidModal();
    this.vehicleService.closeDirectBuyModal();
  }
}
